<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  isAdminDownloadEligible,
  isAttachmentBlocked,
} from 'shared/helpers/attachmentSafety';

import { useMessageContext } from '../provider.js';
import BaseAttachmentBubble from './BaseAttachment.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';

const { attachments, messageType } = useMessageContext();

const { t } = useI18n();
const store = useStore();
const isDescribing = ref(false);
const isReleasing = ref(false);
const { isAdmin } = useAdmin();
const { checkPermissions } = usePolicy();

const attachment = computed(() => attachments.value[0]);
const attachmentDescription = ref(
  attachment.value?.attachmentDescription || ''
);

const url = computed(() => {
  return attachment.value.dataUrl;
});

const isBlocked = computed(() => isAttachmentBlocked(attachment.value));

const fileName = computed(() => {
  if (attachment.value?.filename) {
    return attachment.value.filename;
  }
  if (url.value) {
    const filename = url.value.substring(url.value.lastIndexOf('/') + 1);
    return filename || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  }
  return t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileType = computed(() => {
  return fileName.value.split('.').pop();
});

const decodedFileName = computed(() => {
  try {
    return decodeURI(fileName.value);
  } catch {
    return fileName.value;
  }
});

const fileTypeLabel = computed(() =>
  fileType.value
    ? fileType.value.toUpperCase()
    : t('CONVERSATION.REPLYBOX.GENERIC_DOCUMENT')
);

const accountId = computed(() => store.getters.getCurrentAccountId);
const canManageSecurity = computed(
  () => isAdmin.value || checkPermissions(['security_manage'])
);
const canReleaseAttachment = computed(
  () => canManageSecurity.value && isAdminDownloadEligible(attachment.value)
);
const canDescribeAttachment = computed(
  () =>
    messageType.value === 0 &&
    !isBlocked.value &&
    store.getters['accounts/isFeatureEnabledonAccount'](
      accountId.value,
      FEATURE_FLAGS.LUMERAI_TASKS
    )
);

const announce = (message, priority = 'polite') => {
  window.dispatchEvent(
    new CustomEvent('a11y-announce', { detail: { message, priority } })
  );
};

const describeAttachment = async () => {
  if (isDescribing.value) return;

  try {
    isDescribing.value = true;
    announce(t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_LOADING'));
    const { data } = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/lumerai/tasks/describe_attachment`,
      { attachment_id: attachment.value.id }
    );
    attachmentDescription.value = data.description;
    if (data.cached) {
      useAlert(t('CONVERSATION.REPLYBOX.DESCRIPTION_FROM_CACHE'));
    }
    announce(
      t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_ANNOUNCEMENT', {
        description: data.description,
      }),
      'assertive'
    );
  } catch (error) {
    const message =
      error.response?.data?.error ||
      t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_ERROR');
    useAlert(message);
    announce(message, 'assertive');
  } finally {
    isDescribing.value = false;
  }
};

const releaseAttachment = async () => {
  if (!canReleaseAttachment.value || isReleasing.value) return;

  try {
    isReleasing.value = true;

    const { data } = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/lumerai_alerts/review_malware_attachment`,
      { attachment_id: attachment.value.id, decision: 'download_once' }
    );
    if (data.download_url) {
      const downloadLink = document.createElement('a');
      downloadLink.href = data.download_url;
      downloadLink.download = '';
      downloadLink.rel = 'noopener';
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      useAlert(
        'Link temporário criado. O anexo continua bloqueado para a equipe e para a LumerAI.'
      );
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível liberar o anexo.'
    );
  } finally {
    isReleasing.value = false;
  }
};

const action = computed(() => {
  if (isBlocked.value || !url.value) return null;

  return {
    href: url.value,
    label: t('CONVERSATION.DOWNLOAD'),
    ariaLabel: t('CONVERSATION.REPLYBOX.DOWNLOAD_ATTACHMENT', {
      name: decodedFileName.value,
    }),
  };
});
</script>

<template>
  <BaseAttachmentBubble
    icon="i-teenyicons-user-circle-solid"
    icon-bg-color="bg-n-alpha-3 dark:bg-n-alpha-white"
    sender-translation-key="CONVERSATION.SHARED_ATTACHMENT.FILE"
    :content="decodedFileName"
    :aria-label="
      t('CONVERSATION.REPLYBOX.ATTACHMENT_LABEL', {
        type: fileTypeLabel,
        name: decodedFileName,
      })
    "
    :action="action"
  >
    <template #icon>
      <FileIcon :file-type="fileType" class="size-4" />
    </template>
    <p
      v-if="isBlocked"
      role="status"
      class="mb-0 rounded-lg bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
    >
      Anexo bloqueado pela segurança. Não abra nem baixe. O arquivo foi
      encaminhado para revisão administrativa.
    </p>
    <template #after-action>
      <button
        v-if="canReleaseAttachment"
        type="button"
        class="flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border border-n-yellow-8 bg-n-yellow-2 px-3 py-2 text-sm font-medium text-n-yellow-12 hover:bg-n-yellow-3 disabled:cursor-wait disabled:opacity-60"
        :disabled="isReleasing"
        :aria-busy="isReleasing"
        @click="releaseAttachment"
      >
        <Icon aria-hidden="true" icon="i-lucide-shield-check" />
        {{
          isReleasing
            ? 'Gerando link temporário…'
            : 'Liberar download administrativo'
        }}
      </button>
      <button
        v-if="canDescribeAttachment && !attachmentDescription"
        data-a11y-describe-attachment
        type="button"
        class="flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border border-n-container bg-n-alpha-1 px-3 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-wait disabled:opacity-60"
        :disabled="isDescribing"
        :aria-busy="isDescribing"
        @click="describeAttachment"
      >
        <Icon aria-hidden="true" icon="i-lucide-file-search" />
        {{
          isDescribing
            ? t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_LOADING_SHORT')
            : t('CONVERSATION.REPLYBOX.DESCRIBE_ATTACHMENT')
        }}
      </button>
      <p
        v-if="attachmentDescription"
        role="status"
        class="mb-0 whitespace-pre-line rounded-lg bg-n-alpha-1 p-3 text-sm leading-relaxed text-n-slate-11"
      >
        <strong class="text-n-slate-12">
          {{ t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_LABEL') }}
        </strong>
        {{ attachmentDescription }}
      </p>
    </template>
  </BaseAttachmentBubble>
</template>
